'use client';

import { useEffect, useMemo, useRef } from 'react';
import type { HtmlSnippetResolved } from './types';
import { splitHtmlSnippet, substituteSnippet } from '@/lib/htmlSnippet';
import { useSubstitute } from '@/lib/render/RenderContextProvider';

/**
 * Whether the browser will fetch and run this script, from its `type`.
 *
 * It decides whether waiting for `load` is safe. A script the browser treats as
 * data — above all Usercentrics' `type="text/plain"` gating form, which carries
 * a `src` the browser never fetches — fires neither `load` nor `error`, so
 * awaiting one would block every script after it for good.
 *
 * An absent or empty `type` is a classic script. `module` runs. Anything else
 * is a JavaScript MIME type or it is data.
 */
const willExecute = (attrs: Record<string, string>): boolean => {
  const type = attrs.type?.trim().toLowerCase();
  if (!type || type === 'module') {
    return true;
  }
  return type.includes('javascript') || type === 'text/ecmascript';
};

/**
 * Raw HTML an editor placed on the page, rendered unchanged.
 *
 * The markup is server-rendered rather than built in the browser, because a
 * third-party script that scans the document for its own embed element has to
 * find it. The Usercentrics loader is the case this exists for: it runs
 * `async` and looks for `.uc-embed` once the page has parsed.
 *
 * The scripts take the other route, created as elements after mount. See
 * `splitHtmlSnippet` for why the two cannot share one path.
 *
 * Nothing here gates a script on consent. That decision sits with the editor,
 * who opts in by pasting the script in Usercentrics' blocking form — the
 * attributes reach the page unchanged, so the CMP picks it up (60.03).
 */
export function HtmlSnippetComponent({
  resolved,
}: {
  resolved: HtmlSnippetResolved;
}) {
  const sub = useSubstitute();
  // Its own element, with no React children, so the appended script nodes
  // never sit in a parent React reconciles.
  const scriptSlotRef = useRef<HTMLDivElement>(null);
  const injectedKeyRef = useRef<string | null>(null);

  // `{query.*}` stays literal here — it is the one namespace a visitor
  // controls. See `substituteSnippet`.
  const { markup, scripts } = useMemo(
    () => splitHtmlSnippet(substituteSnippet(resolved.html, sub)),
    [resolved.html, sub],
  );

  // Identifies the script set, so the effect can tell "React ran me twice" from
  // "the editor changed the snippet". A mount-only guard cannot: it would leave
  // the CMS preview showing the first snippet's scripts forever.
  const scriptsKey = useMemo(() => JSON.stringify(scripts), [scripts]);

  useEffect(() => {
    const slot = scriptSlotRef.current;
    if (!slot || injectedKeyRef.current === scriptsKey) {
      return;
    }
    injectedKeyRef.current = scriptsKey;
    // A previous snippet's nodes. Removing them cannot undo what those scripts
    // already did — this only stops them piling up in the preview.
    slot.replaceChildren();

    void (async () => {
      for (const { attrs, code } of scripts) {
        // The widget unmounted, or a newer snippet took over while we waited.
        if (!slot.isConnected || injectedKeyRef.current !== scriptsKey) {
          return;
        }

        const element = document.createElement('script');
        for (const [name, value] of Object.entries(attrs)) {
          element.setAttribute(name, value);
        }
        if (code) {
          element.textContent = code;
        }

        // A script element the parser did not create is force-async by spec,
        // so several `src` scripts would run in load order and the editor's
        // order would not hold (AC 3). Opting out restores it. An editor who
        // wrote `async` themselves keeps it. `defer` has no effect on a
        // dynamically inserted script and is left as the editor wrote it.
        const wantsAsync = 'async' in attrs;
        if (!wantsAsync) {
          element.async = false;
        }

        // `async = false` orders the `src` scripts against each other, but an
        // inline script runs the moment it is appended — so a snippet that
        // loads a library and then calls it needs the load awaited.
        const awaitLoad =
          Boolean(attrs.src) && !wantsAsync && willExecute(attrs);
        const loaded = awaitLoad
          ? new Promise<void>((resolve) => {
              element.addEventListener('load', () => resolve(), { once: true });
              element.addEventListener('error', () => resolve(), {
                once: true,
              });
            })
          : null;

        slot.appendChild(element);
        if (loaded) {
          await loaded;
        }
      }
    })();
  }, [scripts, scriptsKey]);

  if (!markup.trim() && scripts.length === 0) {
    return null;
  }

  return (
    <div className="pt-4">
      <div data-strapi-field="html">
        <div dangerouslySetInnerHTML={{ __html: markup }} />
        <div ref={scriptSlotRef} />
      </div>
    </div>
  );
}
